/** @jsxImportSource @emotion/react */
import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import concat from 'lodash/concat'
import isArray from 'lodash/isArray'
import uniqBy from 'lodash/uniqBy'
import { GenericFetchContextType } from '../contextFetch/types'
import renderIf from '@signal/utils/renderIf'
import stripDashesFromGuid from '@signal/utils/stripDashesFromGuid'
import getLogger from '../fe-logging'
import AccountPermissionsLoader from '../permissions/Loader'
import { useUserAccounts, useDealerDetails } from './hooks'
import {
  getRecentlyViewedAccountsFromStorage,
  setRecentlyViewedAccountsInStorage
} from './storage'
import {
  AccountChangeHandlerType,
  UserAccountType,
  UserAccountsType,
  RecentlyViewedAccountsType,
  UserAccountsFetchHookType,
  DealerDetailsFetchHookType
} from './types'
import {
  ActiveAccountContext,
  UserAccountsContext,
  UserAccountsFetcher,
  UserDetailsFetcher,
  DealerDetailsFetcher,
  DealerDetailsContext
} from './Context'

const MAX_STORED_RECENT_ACCOUNTS = 10

/**
 * @interface IActiveAccountSyncProps
 * this interface defines the properties accepted by the ActiveAccountSync component
 */
export interface IActiveAccountSyncProps {
  // the current account id from the parent application
  accountId?: string
  // children from React
  children?: React.ReactNode
  UserAccountsFetchContext?: React.Context<GenericFetchContextType>
}

/**
 * @component DefaultActiveAccountSetter
 * The <ActiveAccountSetter> React component, which ensures the active account is the one whose accountId is in props
 */
function ActiveAccountSync({
  accountId,
  children,
  UserAccountsFetchContext = UserAccountsContext
}: IActiveAccountSyncProps): JSX.Element {
  const { response } = useContext(UserAccountsFetchContext)
  const context = useContext(ActiveAccountContext)
  const { activeAccount, changeActiveAccount } = context
  const activeAccountId =
    activeAccount && stripDashesFromGuid(activeAccount.accountId)

  useEffect(() => {
    if (
      accountId &&
      changeActiveAccount &&
      (!activeAccountId ||
        stripDashesFromGuid(activeAccountId) !==
          stripDashesFromGuid(accountId)) &&
      isArray(response)
    ) {
      const account = ((response as unknown) as UserAccountsType).find(
        ({ accountId: id }) =>
          stripDashesFromGuid(id) === stripDashesFromGuid(accountId)
      )
      if (account) {
        changeActiveAccount(account, true /*fromSync*/)
      }
    }
  }, [accountId, activeAccountId, changeActiveAccount, response])

  return <>{children}</>
}

/**
 * @interface IUserLoaderParams
 * this interface defines the properties accepted by the UserLoader component
 */
export interface UserLoaderPropsType extends IActiveAccountSyncProps {
  accountId?: string
  loadPermissions?: boolean
  // a handler for account changes
  onAccountChange?: AccountChangeHandlerType
  userAccountsFetchHook?: UserAccountsFetchHookType
  UserAccountsFetchContext?: React.Context<GenericFetchContextType>

  dealerDetailsFetchHook?: DealerDetailsFetchHookType
  DealerDetailsFetchContext?: React.Context<GenericFetchContextType>
}

/**
 * @component UserLoader
 * The <UserRoot> React component, which fetches user data using the context fetcher
 */
export function UserLoader({
  accountId,
  children,
  loadPermissions,
  onAccountChange,
  userAccountsFetchHook = useUserAccounts,
  UserAccountsFetchContext = UserAccountsContext,
  dealerDetailsFetchHook = useDealerDetails,
  DealerDetailsFetchContext = DealerDetailsContext
}: UserLoaderPropsType): JSX.Element {
  const [activeAccount, setActiveAccount] = useState<UserAccountType | null>(
    null
  )
  const [recentAccounts, setRecentAccounts] = useState<
    RecentlyViewedAccountsType
  >(getRecentlyViewedAccountsFromStorage())

  const activeAccountContext = useMemo(
    () => ({
      activeAccount,
      recentAccounts: recentAccounts.filter(
        ({ accountId }) =>
          !activeAccount || accountId !== activeAccount.accountId
      ),
      changeActiveAccount: (
        account: UserAccountType,
        fromSync = false
      ): void => {
        const newRecentAccounts = uniqBy(
          concat(
            [
              {
                accountId: account.accountId,
                accountName: account.accountName
              }
            ],
            recentAccounts
          ),
          account => account.accountId
        )
          .slice(0, MAX_STORED_RECENT_ACCOUNTS)
          .filter(Boolean)
        setRecentlyViewedAccountsInStorage(newRecentAccounts)
        setRecentAccounts(newRecentAccounts)

        if (!fromSync) {
          if (!onAccountChange) {
            getLogger().error(
              'could not change account due to missing callback'
            )
          } else {
            onAccountChange(account)

            // allow the onAccountChange to process first before changing this state, to avoid
            // the active account sync from being triggered too soon
            setTimeout(() => {
              setActiveAccount(account)
            })
          }
        } else {
          setActiveAccount(account)
        }
      }
    }),
    [activeAccount, onAccountChange, recentAccounts]
  )

  return (
    <ActiveAccountContext.Provider value={activeAccountContext}>
      <UserAccountsFetcher
        fetchHook={userAccountsFetchHook}
        FetchContext={UserAccountsFetchContext}
        accountId={accountId}
      >
        <DealerDetailsFetcher
          fetchHook={dealerDetailsFetchHook}
          FetchContext={DealerDetailsFetchContext}
          accountId={accountId}
        >
          <UserDetailsFetcher>
            <ActiveAccountSync
              accountId={accountId}
              UserAccountsFetchContext={UserAccountsFetchContext}
            >
              {renderIf(
                Boolean(loadPermissions),
                () => (
                  <AccountPermissionsLoader>
                    {children}
                  </AccountPermissionsLoader>
                ),
                () => children
              )}
            </ActiveAccountSync>
          </UserDetailsFetcher>
        </DealerDetailsFetcher>
      </UserAccountsFetcher>
    </ActiveAccountContext.Provider>
  )
}

export default memo(UserLoader)
